import { AddIcon } from "@cmsgov/ds-medicare-gov";
import classNames from "classnames";
import React, { FunctionComponent, useContext } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import { Pharmacy, PlanType } from "../../../@types";
import routes from "../../../app/routes";
import { AppContext } from "../../../app/store/AppStore";
import { useTranslate } from "../../../helpers";
import { pharmacyAddressBuilder } from "../../SelectPharmaciesPage/helpers";
import { PharmacyCard } from "../components";
import { AlertMessage } from "../../../components";
import { alertMessageHierarchy } from "../../../app/contexts/AlertMessaging/constants";
import { useAlertMessaging } from "../../../app/contexts";
import { getMessageToShow } from "../../../app/contexts/AlertMessaging/helpers";
import { AlertMessageHierarchyKeys } from "../../../app/contexts/AlertMessaging/types";
import { PharmacyNetworkLabelHelpDrawer } from "../../../components/HelpDrawers/PharmacyNetworkLabelHelpDrawer";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../../app/contexts/Analytics/types";

interface PharmacyCardsContainerProps {
  hasMailOrderPharmacies: boolean;
  mailOrderNetworkStatus?: {
    inNetwork: boolean;
    preferred: boolean;
  };
  overThirtyDaysSinceLogin?: boolean;
  pharmacies: Pharmacy[];
  planId?: string;
  planType?: PlanType;
  shouldShowPharmacyLabels?: boolean;
  planHasPreferredPharmacies?: boolean;
}

const PharmacyCardsContainer: FunctionComponent<PharmacyCardsContainerProps> = ({
  hasMailOrderPharmacies,
  mailOrderNetworkStatus = {
    inNetwork: false,
    preferred: false,
  },
  pharmacies,
  planId = "",
  planType,
  shouldShowPharmacyLabels = false,
  planHasPreferredPharmacies = false,
}) => {
  const alphabetizedPharmacies = pharmacies.sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const url = window.location.href.split("/summary?")[0];
  const location = useLocation();
  const pharmacyCount = pharmacies.length + (hasMailOrderPharmacies ? 1 : 0);
  const managePharmaciesLinkText =
    pharmacyCount > 0
      ? t("summary_page.pharmacies.buttons.edit_pharmacies")
      : t("summary_page.pharmacies.buttons.add_pharmacies");
  const {
    state: { messages },
  } = useAlertMessaging();

  const messageToShow = getMessageToShow(
    messages,
    alertMessageHierarchy[
      AlertMessageHierarchyKeys.SUMMARY_PAGE__PHARMACY_SECTION
    ],
    t,
    {
      planId,
      routerState: {
        nextRoute: {
          ...location,
        },
      },
      isFromSummaryPage: true,
      planType,
      planHasPreferredPharmacies: planHasPreferredPharmacies,
    }
  );
  return (
    <div>
      <p
        className={classNames(
          "ds-u-measure--base",
          "ds-u-font-size--lg",
          "ds-u-margin-top--0",
          { "mct-hide-print": pharmacyCount > 0 }
        )}
      >
        {pharmacyCount === 1 ? (
          t("summary_page.pharmacies.sub_header.one_pharmacy")
        ) : (
          <FormattedMessage
            id="summary_page.pharmacies.sub_header"
            values={{ pharmacyCount }}
          />
        )}
      </p>
      {messageToShow && (
        <AlertMessage
          className="ds-u-margin-y--3 e2e-pharmacy-container-alert-message"
          {...{
            icon: messageToShow.icon,
            body: messageToShow.body,
            action: messageToShow.action,
            title: messageToShow.title,
            condition: messageToShow.key,
          }}
        />
      )}
      {pharmacyCount > 0 && (
        <ul
          className="mct-c-summary-page-pharmacy-cards-container mct-c-summary-page__cards-container"
          role="list"
          aria-labelledby="YourPharmaciesHeading"
        >
          {hasMailOrderPharmacies && (
            <li role="listitem">
              <PharmacyCard
                name={t(
                  "summary_page.pharmacies.pharmacy_card.mail_order_pharmacy"
                )}
                shouldShowPharmacyLabels={shouldShowPharmacyLabels}
                isInNetwork={mailOrderNetworkStatus.inNetwork}
                isPreferred={mailOrderNetworkStatus.preferred}
              />
            </li>
          )}
          {alphabetizedPharmacies.map(pharmacy => (
            <li key={pharmacy.npi} role="listitem">
              <PharmacyCard
                address={pharmacyAddressBuilder(pharmacy)}
                isInNetwork={pharmacy.in_network}
                isPreferred={pharmacy.preferred}
                name={pharmacy.name}
                shouldShowPharmacyLabels={shouldShowPharmacyLabels}
              />
            </li>
          ))}
        </ul>
      )}
      {pharmacyCount > 0 && (
        <PharmacyNetworkLabelHelpDrawer
          toggleClassName="mct-c-summary-page-pharmacy-cards-container__pharmacy-network-label-help-drawer-toggle"
          toggleText={t("pharmacy_network_label.help_drawer.title")}
          analyticText="How do pharmacy networks affect what I pay"
        />
      )}
      <Link
        to={routes.summary.managePharmacies}
        onClick={() =>
          dispatch({
            type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
            settings: {
              button: {
                buttonStyle: AnalyticsButtonStyle.SECONDARY,
                buttonType: AnalyticsButtonType.BUTTON,
                text: managePharmaciesLinkText,
              },
              linkUrl: `${url}${routes.summary.managePharmacies}${location.search}`,
            },
          })
        }
        className="mct-c-sub-section__action-button ds-c-button ds-c-button--solid ds-c-button--alternate"
        data-cy="e2e-pharmacy-container-action-button"
      >
        {pharmacyCount > 0 ? (
          managePharmaciesLinkText
        ) : (
          <>
            <AddIcon className="ds-u-margin-right--1 ds-u-font-size--lg" />
            {managePharmaciesLinkText}
          </>
        )}
      </Link>
    </div>
  );
};

export default PharmacyCardsContainer;
